/**
 * MaxBundle - clicking a product inside a bundle opens that product's page.
 *
 * Two strategies, tried in order:
 *
 *   1. The card already contains a link to /products/  ->  use it.
 *
 *   2. It doesn't. Read the product name printed on the card and resolve it
 *      to a real URL through Shopify's own predictive-search endpoint
 *      (/search/suggest.json). The resolved URL is stored on the card, so
 *      the click itself stays synchronous and never trips a popup blocker.
 *
 * Strategy 2 is what makes this work without configuring anything per page:
 * the same code handles every product page and every bundle, and nothing
 * needs updating when a bundle changes.
 *
 * DEBUG -> logs what it found. OPEN_IN_NEW_TAB false -> same tab.
 */
(function () {
  var OPEN_IN_NEW_TAB = false;
  var DEBUG = false;

  var ROOT = '.mb-product-bundle, .cxbundleBlock, .mb-mix-product-select-wrapper, .cxbundleWrapper, .maxbundle-container';
  var CONTROLS = 'button, select, input, textarea, [role="button"], [role="checkbox"]';
  var RESOLVED = 'data-sk-product-url';

  var cache = {};

  function isSpartanThemeCustomizer(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('.section-bundle-banner, [class*="bundle-banner"], [class*="bundle-grid"], [class*="bundle-set-size"], [id*="BundleBanner"], [id*="bundle_banner"], [id*="bundle_flashcards"], [id*="bundle_set_size"]');
  }

  function log() {
    if (!DEBUG || !window.console) return;
    console.log.apply(console, ['[maxbundle-link]'].concat([].slice.call(arguments)));
  }

  /* ---------- find the card boxes ---------- */
  // A card is the smallest ancestor of an image that also carries text.
  function cardsIn(root) {
    if (isSpartanThemeCustomizer(root)) return [];
    var out = [];
    root.querySelectorAll('img').forEach(function (img) {
      var node = img.parentElement;
      var hops = 0;
      while (node && node !== root && hops < 6) {
        var text = (node.textContent || '').trim();
        if (text.length > 12) {
          if (out.indexOf(node) === -1) out.push(node);
          return;
        }
        node = node.parentElement;
        hops++;
      }
    });
    return out;
  }

  /* ---------- get the product name off a card ---------- */
  function titleOf(card) {
    var el = card.querySelector('[class*="title"], [class*="name"]');
    if (el && el.textContent.trim()) return el.textContent.trim();

    var img = card.querySelector('img[alt]');
    if (img && img.alt && img.alt.trim().length > 5) return img.alt.trim();

    // fall back to the card's own text, minus prices and badges
    var text = (card.textContent || '')
      .replace(/(₹|Rs\.?)\s?[\d,.]+/g, ' ')
      .replace(/you save/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text.length > 5 ? text.slice(0, 90) : null;
  }

  /* ---------- title -> product URL, via Shopify search ---------- */
  function resolve(title) {
    if (cache[title]) return cache[title];
    var url =
      '/search/suggest.json?q=' +
      encodeURIComponent(title) +
      '&resources[type]=product&resources[limit]=1';

    cache[title] = fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var items = data && data.resources && data.resources.results && data.resources.results.products;
        return items && items.length ? items[0].url : null;
      })
      .catch(function () { return null; });

    return cache[title];
  }

  /* ---------- attach URLs to the cards ---------- */
  function prepare(root) {
    if (isSpartanThemeCustomizer(root)) return;
    var cards = cardsIn(root);
    log('cards detected:', cards.length);

    cards.forEach(function (card) {
      if (isSpartanThemeCustomizer(card)) return;
      if (card.hasAttribute(RESOLVED)) return;

      var link = card.querySelector('a[href*="/products/"]');
      if (link && link.href) {
        card.setAttribute(RESOLVED, link.href);
        card.style.cursor = 'pointer';
        log('card already linked ->', link.getAttribute('href'));
        return;
      }

      var title = titleOf(card);
      if (!title) { log('no title found on a card'); return; }

      card.setAttribute(RESOLVED, ''); // claim it so we only look up once
      resolve(title).then(function (url) {
        if (!url) { log('could not resolve:', title); return; }
        card.setAttribute(RESOLVED, url);
        card.style.cursor = 'pointer';
        log('resolved:', title, '->', url);
      });
    });
  }

  // The stretched-link CSS makes the card open via the app's own <a>. If that
  // anchor carries target="_blank" the page still opens in a new tab, and no
  // stylesheet can change an attribute - so clear it here.
  function sameTabLinks() {
    document.querySelectorAll(ROOT).forEach(function (root) {
      if (isSpartanThemeCustomizer(root)) return;
      root.querySelectorAll('a[href*="/products/"][target]').forEach(function (a) {
        a.removeAttribute('target');
        a.removeAttribute('rel');
        log('forced same-tab:', a.getAttribute('href'));
      });
    });
  }

  function scan() {
    document.querySelectorAll(ROOT).forEach(prepare);
    if (!OPEN_IN_NEW_TAB) sameTabLinks();
  }

  /* ---------- click ---------- */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (isSpartanThemeCustomizer(target)) return;
      if (!target.closest(ROOT)) return;
      if (target.closest(CONTROLS)) return;

      var direct = target.closest('a[href]');
      if (direct && /\/products\//.test(direct.getAttribute('href') || '')) return;

      var card = target.closest('[' + RESOLVED + ']');
      var url = card && card.getAttribute(RESOLVED);
      if (!url) { log('no resolved URL for this card yet'); return; }

      event.preventDefault();
      event.stopPropagation();
      log('opening', url);

      if (OPEN_IN_NEW_TAB) {
        var win = window.open(url, '_blank', 'noopener');
        if (win) win.opener = null;
        else window.location.href = url;
      } else {
        window.location.href = url;
      }
    },
    true
  );

  /* ---------- run, and re-run when the app redraws ---------- */
  function start() {
    scan();
    var debounce;
    new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(scan, 300);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  log('loaded');
})();
